import { useMutation, useQueryClient } from "react-query";

import { archiveGoal, type TargetDisposition } from "api/goals";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface ArchiveGoalPayload {
  goalId: number;
  clientId: string;
  rationale: string;
  targetDispositions?: TargetDisposition[];
}

const useArchiveGoal = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: archiveGoalAction,
    isLoading: isArchivingGoal,
    ...rest
  } = useMutation(
    ({ goalId, targetDispositions, rationale }: ArchiveGoalPayload) =>
      archiveGoal(goalId, rationale, targetDispositions),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Goal archived" });

        queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);

        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to archive goal";
        showToast({ message: errorMessage, errorState: true });

        onError(error);
      },
      ...otherOptions,
    }
  );

  return {
    archiveGoalAction,
    isArchivingGoal,
    ...rest,
  };
};

export default useArchiveGoal;
